import { Box, Typography, Alert, Divider } from "@mui/material";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";

import AppDialog from "../../General/Dialog/AppDialog";
import { formatDateTime } from "../../../config/Helper";
import { ATTACHMENT_STATUS_LABELS } from "./detailHelpers";

function buildAttachmentSteps(pivot = {}) {
  const hasGenerated = !!pivot.generated_at;
  const hasPreloaded = !!pivot.preloaded_at;
  const hasUploaded = !!pivot.uploaded_at;

  const generatedState = hasGenerated
    ? "done"
    : hasPreloaded
      ? "error"
      : "pending";

  const preloadedState = hasPreloaded
    ? "done"
    : hasUploaded
      ? "error"
      : "pending";

  const uploadedState = hasUploaded ? "done" : "pending";

  const steps = [
    {
      key: "generated",
      title: "Generado",
      state: generatedState,
      date: pivot.generated_at,
      subtitle:
        generatedState === "error"
          ? "No aplica / no se generó (precarga directa)."
          : null,
    },
    {
      key: "preloaded",
      title: "Precargado",
      state: preloadedState,
      date: pivot.preloaded_at,
      subtitle:
        preloadedState === "error"
          ? "Inconsistente: se cargó sin precarga registrada."
          : null,
    },
    {
      key: "uploaded",
      title: "Cargado",
      state: uploadedState,
      date: pivot.uploaded_at,
      subtitle: null,
    },
  ];

  const lastDoneIndex = [...steps]
    .map((s, i) => (s.state === "done" ? i : -1))
    .reduce((acc, v) => Math.max(acc, v), -1);

  const activeStep = Math.max(0, lastDoneIndex + 1);

  const currentStatusKey = pivot.status || null;

  return { steps, activeStep, currentStatusKey };
}

const MovementsStepIcon = ({ state }) => {
  if (state === "done")
    return <CheckCircleRoundedIcon sx={{ color: "success.main" }} />;
  if (state === "error")
    return <CancelRoundedIcon sx={{ color: "error.main" }} />;
  return <RadioButtonUncheckedRoundedIcon sx={{ color: "text.disabled" }} />;
};

export default function AttachmentMovementsDialog({
  open,
  onClose,
  attachment,
}) {
  const pivot = attachment?.pivot || {};
  const { steps, activeStep, currentStatusKey } = buildAttachmentSteps(pivot);

  const statusKey =
    attachment?.status_label || currentStatusKey || attachment?.status || "—";

  const statusTranslated =
    ATTACHMENT_STATUS_LABELS[String(statusKey).toLowerCase()] ||
    String(statusKey);

  const current =
    currentStatusKey && ATTACHMENT_STATUS_LABELS[currentStatusKey]
      ? ATTACHMENT_STATUS_LABELS[currentStatusKey]
      : statusTranslated;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="Movimientos del adjunto"
      subtitle={`${attachment?.name || "—"} · ${attachment?.type?.name || "—"}`}
      maxWidth="sm"
      stickyHeader
      paperSx={{ borderRadius: 4, overflow: "hidden" }}
    >
      <Box
        sx={{
          p: 1.6,
          borderRadius: 3,
          bgcolor: "rgba(0,0,0,0.035)",
          border: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Typography sx={{ fontWeight: 900 }}>
          Estado actual:{" "}
          <Box component="span" sx={{ color: "primary.main" }}>
            {current}
          </Box>
        </Typography>

        {pivot.error_message ? (
          <Alert severity="error" sx={{ mt: 1.2 }}>
            {pivot.error_message}
          </Alert>
        ) : null}
      </Box>

      <Divider sx={{ opacity: 0.6, my: 2 }} />

      <Stepper activeStep={activeStep} orientation="vertical">
        {steps.map((s) => (
          <Step key={s.key} expanded>
            <StepLabel
              icon={<MovementsStepIcon state={s.state} />}
              sx={{
                "& .MuiStepLabel-label": {
                  fontWeight: 950,
                  ...(s.state === "error" && { color: "error.main" }),
                  ...(s.state === "done" && { color: "text.primary" }),
                },
              }}
            >
              {s.title}
            </StepLabel>

            <StepContent>
              <Box sx={{ pb: 1.2 }}>
                {s.date ? (
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Fecha: {formatDateTime(s.date)}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ opacity: 0.6 }}>
                    Sin registro.
                  </Typography>
                )}

                {s.subtitle ? (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 0.7,
                      color:
                        s.state === "error" ? "error.main" : "text.secondary",
                    }}
                  >
                    {s.subtitle}
                  </Typography>
                ) : null}
              </Box>
            </StepContent>
          </Step>
        ))}
      </Stepper>

      <Divider sx={{ opacity: 0.6, my: 2 }} />

      <Box sx={{ display: "grid", gap: 0.6 }}>
        <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 900 }}>
          Registro
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.85 }}>
          Creado: {formatDateTime(pivot.created_at) || "—"}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.85 }}>
          Actualizado: {formatDateTime(pivot.updated_at) || "—"}
        </Typography>
      </Box>
    </AppDialog>
  );
}
